import { IOption, RootState } from '../../../base';
import { createSelector } from 'reselect';
import {
    IAssignedJobListRecord,
    IJob,
    JobApplicationStatusUpdateValues,
    JobStatus,
} from '../../../common';

export const jobsSelectors = {
    getActiveJob: (state: RootState) => state.jobs.activeJob,
    getActiveJobId: (state: RootState) => state.jobs.activeJob?.id,
    getActiveJobLoading: (state: RootState) => state.jobs.uiState.isJobLoading,
    getCandidateSavedJobs: (state: RootState) => state.jobs.candidateSavedJobs,
    getJobsPageStatistics: (state: RootState) => state.jobs.jobsPageStatistics,
    getJobsPageStatisticsLoading: (state: RootState) =>
        state.jobs.uiState.isJobsPageStatisticsLoading,
    getJobAttributeList: (state: RootState) => state.jobs.jobAttributeList,
    getIsUpdateJobLoading: (state: RootState) =>
        state.jobs.uiState.isUpdateJobLoading,
    getJobStepperInfo: (state: RootState) => state.jobs.jobStepperInfo,
    getJobRequirementsOptions: (state: RootState) =>
        state.jobs.jobRequirementsOptions,
    getCandidatesJobs: (state: RootState) => state.jobs.candidatesJobs,
    getCandidatesJobsLoading: (state: RootState) =>
        state.jobs.uiState.isCandidatesJobsLoading,
    getJobsBoardFilters: (state: RootState) => state.jobs.jobsBoardFilters,
    getIsInitialCandidatesJobsLoading: (state: RootState) =>
        state.jobs.uiState.isInitialCandidatesJobsLoading,
    getJobBenefitsOptions: (state: RootState) => state.jobs.jobBenefitsOptions,
    getJobById: (state: RootState, jobId: number | null) =>
        selectJobById(state, jobId),
    getAssignedJobList: (state: RootState) => state.jobs.assignedJobList,
    getAssignedJobListOptions: (state: RootState) => assignedJobList(state),
    getTrackableSources: (state: RootState) => state.jobs.trackableSources,
    getTrackableSourcesOptions: (state: RootState) =>
        trackableSourcesOptions(state),
    getPublicTrackableSources: (state: RootState) =>
        state.jobs.publicTrackableSources,
    getIsTrackableSourcesLoading: (state: RootState) =>
        state.jobs.uiState.isTrackableSourcesLoading,
    getIsPublicTrackableSourcesLoading: (state: RootState) =>
        state.jobs.uiState.isPublicTrackableSourcesLoading,
    getJobsList: (state: RootState) => state.jobs.jobsList,
    getNonDraftJobsList: (state: RootState) => getNonDraftJobsList(state),
    getFormattedJobSteps: (state: RootState) => getFormattedJobSteps(state),
    getCancelOrCloseJob: (state: RootState) => state.jobs.cancelOrCloseJob,
    getIsConfirmJobCloseOrCancelModalOpen: (state: RootState) =>
        state.jobs.uiState.isConfirmJobCloseOrCancelModalOpen,
    getJobRequirementsRecomendations: (state: RootState) =>
        state.jobs.jobRequirementsRecomendations,
    getFormattedJobRequirementsRecomendations: (state: RootState) =>
        state.jobs.formattedJobRequirementsRecomendations,
    getIsJobRequirementsRecommendationsLoading: (state: RootState) =>
        state.jobs.uiState.isJobRequirementsRecommendationsLoading,
    getIsProceedWithRecommendedModalOpen: (state: RootState) =>
        state.jobs.uiState.isProceedWithRecommendedModalOpen,
    getPublishedJobsOptions: (state: RootState) =>
        getPublishedJobsOptions(state),
    getPublishedJobs: (state: RootState) => state.jobs.publishedJobs,
    getIsPublishedJobsOptionsLoading: (state: RootState) =>
        state.jobs.uiState.isPublishedJobsOptionsLoading,
    getJobOverviewAndResponsibilities: (state: RootState) =>
        state.jobs.jobOverviewAndResponsibilitiesRecommmendation,
    getIsJobOverviewAndResponsibilitiesLoading: (state: RootState) =>
        state.jobs.uiState.isJobOverviewAndResponsibilitiesLoading,
    getActiveJobCandidates: (state: RootState) =>
        state.jobs.activeJobCandidates,
    getIsActiveJobCandidatesLoading: (state: RootState) =>
        state.jobs.uiState.isActiveJobCandidatesLoading,
    getActiveCandidateJobId: (state: RootState) =>
        state.jobs.activeCandidateJobId,
    getActiveJobRequirementRecommendations: (state: RootState) =>
        state.jobs.activeJobRequirementRecommendations,
    getIsRecommendationsExhausted: (state: RootState) =>
        state.jobs.isRecommendationsExhausted,
    getCopiedJobSteps: (state: RootState) => state.jobs.copiedJobSteps,
};

const selectJobById = createSelector(
    [
        jobsSelectors.getCandidatesJobs,
        (_state: RootState, jobId: number | null) => jobId,
    ],
    (candidatesJobs, jobId) => {
        if (!jobId) return null;
        return candidatesJobs?.find((job) => job.id === jobId);
    }
);

const assignedJobList = createSelector(
    [jobsSelectors.getAssignedJobList],
    (jobList): Array<IOption> => {
        return jobList?.map((job: IAssignedJobListRecord) => {
            return {
                key: String(job.id),
                label: `${job.id} - ${job.title}`,
                value: String(job.id),
            };
        });
    }
);

const trackableSourcesOptions = createSelector(
    [jobsSelectors.getTrackableSources],
    (trackableSources): Array<IOption> =>
        Object.keys(trackableSources).map((name) => ({
            key: name,
            label: name,
            value: name,
        }))
);

const getFormattedJobSteps = createSelector(
    [jobsSelectors.getActiveJob],
    (job: IJob | null) =>
        job?.steps?.map((step) => ({
            id: step.id,
            name: step.name,
            users: [
                {
                    id: step.stepOwner.value || '',
                    name: step.stepOwner.label,
                    avatar: '',
                    decision: null,
                    isOwner: true,
                },
            ],
            status: JobApplicationStatusUpdateValues.inProcess,
        })) || []
);

const getPublishedJobsOptions = createSelector(
    [jobsSelectors.getPublishedJobs],
    (publishedJobs): Array<IOption> =>
        publishedJobs.map((job) => ({
            label: `${job.title} (${job.id})`,
            value: String(job.id),
        }))
);

const getNonDraftJobsList = createSelector(
    [jobsSelectors.getJobsList],
    (jobsList): Array<IOption> =>
        jobsList
            .filter((job) => job.status !== JobStatus.Draft)
            .map((job) => ({
                label: job.label,
                value: job.value,
            }))
);
